import { useCallback, useMemo } from "react";
import { MsalProvider, useMsal } from "@azure/msal-react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { buildMsal, API_SCOPE } from "./auth/msal.js";
import { AuthGate } from "./auth/AuthGate.js";
import { buildAdminApi } from "./api/client.js";
import { AppointmentsPage } from "./pages/AppointmentsPage.js";
import { AppointmentDetailPage } from "./pages/AppointmentDetailPage.js";
import { CalendarConfigPage } from "./pages/CalendarConfigPage.js";
import { SettingsHubPage } from "./pages/SettingsHubPage.js";
import { AuditLogPage } from "./pages/AuditLogPage.js";
import { AccountPage } from "./pages/AccountPage.js";
import { HealthPage } from "./pages/HealthPage.js";
import { CalendarTroubleshootPage } from "./pages/CalendarTroubleshootPage.js";
import { Layout } from "./components/Layout.js";

const msal = buildMsal();
const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 30_000 } },
});

export function App() {
  return (
    <MsalProvider instance={msal}>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AuthGate>
            <Authed />
          </AuthGate>
        </BrowserRouter>
      </QueryClientProvider>
    </MsalProvider>
  );
}

function Authed() {
  const { instance, accounts } = useMsal();
  const account = accounts[0];
  const userEmail = account?.username;

  const getToken = useCallback(async () => {
    const acct = accounts[0];
    if (!acct) throw new Error("Not signed in");
    const result = await instance.acquireTokenSilent({
      scopes: [API_SCOPE],
      account: acct,
    });
    return result.accessToken;
  }, [instance, accounts]);

  const api = useMemo(
    () => buildAdminApi(import.meta.env.VITE_API_BASE_URL, getToken),
    [getToken],
  );

  return (
    <Layout
      userEmail={userEmail}
      onSignOut={() => {
        void instance.logoutRedirect();
      }}
    >
      <Routes>
        <Route path="/" element={<AppointmentsPage api={api} />} />
        <Route
          path="/appointments/:id"
          element={<AppointmentDetailPage api={api} />}
        />
        <Route path="/settings" element={<SettingsHubPage />} />
        <Route
          path="/settings/calendar"
          element={<CalendarConfigPage api={api} />}
        />
        <Route
          path="/settings/audit"
          element={<AuditLogPage api={api} />}
        />
        <Route path="/settings/account" element={<AccountPage />} />
        <Route path="/settings/health" element={<HealthPage api={api} />} />
        <Route
          path="/settings/troubleshoot"
          element={<CalendarTroubleshootPage api={api} />}
        />
      </Routes>
    </Layout>
  );
}
